import { SeededRandom } from '@/core/Random';
import type { FaunaDefinition } from '@/fauna/FaunaDefinition';

/**
 * The pure half of placing staffage: where a group lands, and which way each member faces.
 *
 * Separated from the tool for the reason the terrain brushes already are — this is arithmetic with a
 * right answer, and it is worth being able to test it without a canvas, a camera, or a raycast.
 */

export interface ScatterPoint {
  /** Offset from the gesture's point, in meters. */
  dx: number;
  dz: number;
  /** Heading in radians. */
  rotationY: number;
  /** Signed fraction within the record's own variation band. */
  sizeVariation: number;
  /** Per-member seed, so a member's own randomisation is stable across rebuilds. */
  visualSeed: number;
}

export interface ScatterOptions {
  /** How many to place. Clamped to at least 1. */
  count: number;
  /** Roughly how far the group spreads, edge to edge, in meters. */
  spreadMeters: number;
  /** The record's variation band. 0 leaves every member the same size. */
  sizeVariation?: number;
  seed: number;
}

/**
 * Scatter a group around a point.
 *
 * The first member always lands exactly where the pointer was — a designer who clicks on a spot
 * expects *something* to be on that spot, and a group whose every member was jittered would drift
 * off the paving slab they were aimed at. Only the rest of the flock is scattered.
 *
 * Offsets are sampled on a disc by the square-root method rather than uniformly in radius, which is
 * what keeps a flock from bunching at its centre. Headings are fully random: birds on a lawn do not
 * face the same way, and a row of identically-oriented deer is the single most obvious tell that a
 * scene was placed by a machine.
 */
export function scatterStaffage(options: ScatterOptions): ScatterPoint[] {
  const count = Math.max(1, Math.floor(options.count));
  const radius = Math.max(0, options.spreadMeters) / 2;
  const band = Math.max(0, options.sizeVariation ?? 0);
  const random = new SeededRandom(options.seed);
  const points: ScatterPoint[] = [];

  for (let index = 0; index < count; index += 1) {
    const centred = index === 0;
    const distance = centred ? 0 : radius * Math.sqrt(random.next());
    const angle = random.range(0, Math.PI * 2);
    points.push({
      dx: centred ? 0 : Math.cos(angle) * distance,
      dz: centred ? 0 : Math.sin(angle) * distance,
      rotationY: random.range(0, Math.PI * 2),
      sizeVariation: band === 0 ? 0 : random.range(-band, band),
      visualSeed: random.int(1, 0x7ffffff),
    });
  }
  return points;
}

/**
 * How many of a record to place in one gesture.
 *
 * `typical` by default — the number the record says you would actually see — and exactly one when
 * the designer asks for one. That is the whole of it: a "how many" slider would be a control for a
 * decision the catalog has already made better than a default could.
 */
export function groupSizeFor(definition: FaunaDefinition, single: boolean): number {
  if (single) return 1;
  return Math.max(1, Math.min(definition.grouping.typical, definition.grouping.maximum));
}

/**
 * A deterministic heading for a lone placement.
 *
 * Random rather than zero, because a creature is not a bench: a rabbit facing due north every time
 * reads as a repeated asset the moment there are two of them. Objects take the opposite default —
 * see `Application.placeProp`, where a row of benches facing the same way is exactly right.
 */
export function headingFor(seed: number): number {
  return new SeededRandom(seed).range(0, Math.PI * 2);
}
